/**
 * TTSService.js — Clara Voice Output
 * CLARA360-VOICE-RECOVERY-1
 *
 * Primär: Atlas Edge-TTS Server (de-DE-KatjaNeural) via /api/tts/
 * Fallback 1: Browser Google Deutsch (falls verfügbar)
 * Fallback 2: Browser Standard-TTS
 *
 * Voice Policy:
 * - Standard: de-DE-KatjaNeural (weiblich, serverseitig, deterministisch)
 * - Optional: de-DE-ConradNeural (männlich, serverseitig)
 * - Experimentell: Browser Google Deutsch
 */

class TTSService {
  constructor() {
    this.synth = window.speechSynthesis;
    this.browserVoice = null;
    this.isInitialized = false;
    this.currentAudio = null;
    this.isSpeaking = false;

    // Voice Policy
    this.voicePolicy = {
      primary: 'server',          // 'server' | 'browser'
      serverVoice: 'de-DE-KatjaNeural',
      serverRate: '1.0',
      serverEndpoint: '/api/tts/',
    };
  }

  // ─────────────────────────────────────────────────────────────
  // Initialisierung
  // ─────────────────────────────────────────────────────────────
  async initialize() {
    // Server-TTS prüfen
    try {
      const resp = await fetch(
        `${this.voicePolicy.serverEndpoint}?text=Test&voice=${this.voicePolicy.serverVoice}&lang=de&rate=${this.voicePolicy.serverRate}`,
        { method: 'GET', signal: AbortSignal.timeout(5000) }
      );
      if (resp.ok) {
        this.voicePolicy.primary = 'server';
        console.log('[TTS] Server-TTS verfügbar:', this.voicePolicy.serverVoice);
        this.isInitialized = true;
        return true;
      }
    } catch (e) {
      console.warn('[TTS] Server-TTS nicht erreichbar, Browser-Fallback:', e.message);
    }

    // Browser-Fallback
    return this._initBrowserTTS();
  }

  _initBrowserTTS() {
    return new Promise((resolve) => {
      if (!this.synth) {
        this.isInitialized = true;
        resolve(false);
        return;
      }
      const loadVoices = () => {
        const voices = this.synth.getVoices();
        // Google Deutsch weiblich bevorzugen
        this.browserVoice =
          voices.find(v => v.lang.startsWith('de') && v.name.toLowerCase().includes('google') && v.name.toLowerCase().includes('female')) ||
          voices.find(v => v.lang.startsWith('de') && v.name.toLowerCase().includes('google')) ||
          voices.find(v => v.lang.startsWith('de'));

        this.voicePolicy.primary = 'browser';
        this.isInitialized = true;
        console.log('[TTS] Browser-TTS Stimme:', this.browserVoice?.name || 'Standard');
        resolve(!!this.browserVoice);
      };
      if (this.synth.getVoices().length > 0) {
        loadVoices();
      } else {
        this.synth.addEventListener('voiceschanged', loadVoices, { once: true });
        // Timeout-Fallback: nach 2s trotzdem initialisieren
        setTimeout(() => {
          if (!this.isInitialized) {
            this.voicePolicy.primary = 'browser';
            this.isInitialized = true;
            resolve(false);
          }
        }, 2000);
      }
    });
  }

  // ─────────────────────────────────────────────────────────────
  // Sprechen
  // ─────────────────────────────────────────────────────────────
  async speak(text, options = {}) {
    if (!text || !this.isInitialized) return;
    this.stop();

    const voice = options.voice || this.voicePolicy.serverVoice;
    const rate = options.rate || this.voicePolicy.serverRate;

    if (this.voicePolicy.primary === 'server') {
      await this._speakServer(text, voice, rate);
    } else {
      this._speakBrowser(text);
    }
  }

  async _speakServer(text, voice, rate) {
    try {
      const url = `${this.voicePolicy.serverEndpoint}?text=${encodeURIComponent(text)}&voice=${voice}&lang=de&rate=${rate}`;
      const resp = await fetch(url, { signal: AbortSignal.timeout(15000) });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      const blob = await resp.blob();
      const audioUrl = URL.createObjectURL(blob);
      this.currentAudio = new Audio(audioUrl);
      this.isSpeaking = true;

      this.currentAudio.onended = () => {
        this.isSpeaking = false;
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
      };
      this.currentAudio.onerror = (e) => {
        console.error('[TTS] Audio playback error:', e);
        this.isSpeaking = false;
        // Fallback auf Browser
        this._speakBrowser(text);
      };

      await this.currentAudio.play();
      console.log('[TTS] Server spricht (Katja):', text.substring(0, 50));
    } catch (e) {
      console.warn('[TTS] Server-TTS fehlgeschlagen, Browser-Fallback:', e.message);
      this._speakBrowser(text);
    }
  }

  _speakBrowser(text) {
    if (!this.synth) return;
    const utterance = new SpeechSynthesisUtterance(text);
    if (this.browserVoice) utterance.voice = this.browserVoice;
    utterance.lang = 'de-DE';
    utterance.rate = 0.9;
    utterance.pitch = 1.1;
    utterance.volume = 0.8;
    utterance.onerror = (e) => console.error('[TTS] Browser-Fehler:', e.error);
    utterance.onstart = () => { this.isSpeaking = true; };
    utterance.onend = () => { this.isSpeaking = false; };
    this.synth.speak(utterance);
  }

  // ─────────────────────────────────────────────────────────────
  // Stopp
  // ─────────────────────────────────────────────────────────────
  stop() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    if (this.synth) this.synth.cancel();
    this.isSpeaking = false;
  }

  // ─────────────────────────────────────────────────────────────
  // Stimme wechseln (für Einstellungsseite)
  // ─────────────────────────────────────────────────────────────
  setVoice(voiceId) {
    if (voiceId === 'browser') {
      this.voicePolicy.primary = 'browser';
    } else {
      this.voicePolicy.primary = 'server';
      this.voicePolicy.serverVoice = voiceId; // z.B. 'de-DE-ConradNeural'
    }
  }

  getActiveVoiceName() {
    if (this.voicePolicy.primary === 'server') return this.voicePolicy.serverVoice;
    return this.browserVoice?.name || 'Browser Standard';
  }
}

export default new TTSService();
